import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import * as userService from '../../../services/userService';
import { validateEmail, validatePreFilledData } from '../../../services/inviteService';
import toast from 'react-hot-toast';

/**
 * InviteForm Component
 * Form for creating new invites with optional profile pre-population
 */
const InviteForm = ({ onSave, onCancel, loading, isModal = false }) => {
  const { userRole, user } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    expiresInDays: 7,
    role: 'CLIENT',
    first_name: '',
    last_name: '',
    full_name: '',
    coach_id: ''
  });
  const [coaches, setCoaches] = useState([]);
  const [errors, setErrors] = useState({});
  const [enablePreFill, setEnablePreFill] = useState(false);

  // Fetch coaches when component mounts
  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const { data, error } = await userService.getCoaches();
        if (error) {
          console.error('Error fetching coaches:', error);
          toast.error('Failed to load coaches');
        } else {
          // If user is a coach, only show themselves in the list
          if (userRole === 'COACH' && user?.id) {
            const currentCoach = (data || []).find(coach => coach.id === user.id);
            setCoaches(currentCoach ? [currentCoach] : []);
            
            // Auto-select the current coach when pre-fill is enabled
            if (currentCoach) {
              setFormData(prev => ({
                ...prev,
                coach_id: currentCoach.id
              }));
            }
          } else {
            setCoaches(data || []);
          }
        }
      } catch (err) {
        console.error('Error fetching coaches:', err);
        toast.error('Failed to load coaches');
      }
    };

    fetchCoaches();
  }, [userRole, user?.id]);

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Expiration validation
    if (!formData.expiresInDays || formData.expiresInDays < 1 || formData.expiresInDays > 30) {
      newErrors.expiresInDays = 'Expiration must be between 1 and 30 days';
    }

    // Pre-filled data validation (only if pre-fill is enabled)
    if (enablePreFill) {
      const preFilledData = {
        role: formData.role,
        first_name: formData.first_name,
        last_name: formData.last_name,
        full_name: formData.full_name,
        coach_id: formData.coach_id || null
      };

      const validation = validatePreFilledData(preFilledData);
      if (!validation.valid) {
        newErrors.preFilledData = validation.errors.join(', ');
      }

      // Role-specific validations
      if (userRole === 'COACH') {
        // Coaches can only assign CLIENT role
        if (formData.role !== 'CLIENT') {
          newErrors.role = 'Coaches can only invite clients';
        }
        // Coaches should auto-assign themselves as coach
        if (!formData.coach_id) {
          // This will be handled automatically in form submission
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      // Auto-generate full_name when first_name or last_name changes
      if (name === 'first_name' || name === 'last_name') {
        const firstName = name === 'first_name' ? value : prev.first_name;
        const lastName = name === 'last_name' ? value : prev.last_name;
        newFormData.full_name = `${firstName} ${lastName}`.trim();
      }

      return newFormData;
    });

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      let preFilledData = null;
      
      if (enablePreFill) {
        preFilledData = {
          role: formData.role,
          first_name: formData.first_name || null,
          last_name: formData.last_name || null,
          full_name: formData.full_name || null,
          coach_id: formData.coach_id || null
        };

        // For coaches, auto-assign themselves if no coach is selected
        if (userRole === 'COACH' && !preFilledData.coach_id) {
          // This will be handled by the AuthContext to get current user ID
          preFilledData.coach_id = null; // Will be set in the service call
        }

        // Remove null/empty values to keep the JSON clean
        Object.keys(preFilledData).forEach(key => {
          if (preFilledData[key] === null || preFilledData[key] === '') {
            delete preFilledData[key];
          }
        });

        if (Object.keys(preFilledData).length === 0) {
          preFilledData = null;
        }
      }

      const submissionData = {
        email: formData.email.trim(),
        preFilledData,
        expiresInDays: parseInt(formData.expiresInDays)
      };

      await onSave(submissionData);
      
      // Reset form on success
      setFormData({
        email: '',
        expiresInDays: 7,
        role: 'CLIENT',
        first_name: '',
        last_name: '',
        full_name: '',
        coach_id: ''
      });
      setEnablePreFill(false);
      setErrors({});
      
    } catch (error) {
      console.error("Failed to send invite:", error);
      setErrors({ general: error.message || "Failed to send invite. Please try again." });
    }
  };

  const handleTogglePreFill = (enabled) => {
    setEnablePreFill(enabled);
    if (!enabled) {
      // Clear pre-fill data when disabled
      setFormData(prev => ({
        ...prev,
        role: 'CLIENT',
        first_name: '',
        last_name: '',
        full_name: '',
        coach_id: ''
      }));
      // Clear related errors
      const { role, first_name, last_name, full_name, coach_id, preFilledData, ...remainingErrors } = errors;
      setErrors(remainingErrors);
    }
  };

  return (
    <div className={isModal ? '' : 'bg-white shadow rounded-lg p-4'}>
      {!isModal && <h2 className="text-xl font-bold mb-4">Send New Invite</h2>}
      
      {errors.general && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            id="email"
            value={formData.email}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
              errors.email ? 'border-red-500' : ''
            }`}
            placeholder="Enter email address to invite"
            required
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        {/* Expiration Field */}
        <div>
          <label htmlFor="expiresInDays" className="block text-sm font-medium text-gray-700">
            Expires In (Days) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="expiresInDays"
            id="expiresInDays"
            value={formData.expiresInDays}
            onChange={handleChange}
            min="1"
            max="30"
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
              errors.expiresInDays ? 'border-red-500' : ''
            }`}
            required
          />
          {errors.expiresInDays && (
            <p className="mt-1 text-sm text-red-600">{errors.expiresInDays}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            The invite link will expire after this many days (1-30 days)
          </p>
        </div>

        {/* Pre-fill Toggle */}
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="enablePreFill"
            checked={enablePreFill}
            onChange={(e) => handleTogglePreFill(e.target.checked)}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="enablePreFill" className="text-sm font-medium text-gray-700">
            Pre-fill user profile information
          </label>
        </div>
        <p className="text-xs text-gray-500 ml-7">
          When enabled, the registration form will be pre-populated with the information below
        </p>

        {/* Pre-fill Fields - Only show when enabled */}
        {enablePreFill && (
          <div className="border border-gray-200 rounded-md p-4 bg-gray-50 space-y-4">
            <h4 className="font-medium text-gray-900">Profile Pre-fill Information</h4>
            
            {errors.preFilledData && (
              <div className="p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                {errors.preFilledData}
              </div>
            )}

            {/* Role Field */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                User Role
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                disabled={userRole === 'COACH'} // Coaches can only invite clients
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                  userRole === 'COACH' ? 'bg-gray-100' : ''
                } ${errors.role ? 'border-red-500' : ''}`}
              >
                <option value="CLIENT">Client</option>
                {userRole === 'SUPER_ADMIN' && (
                  <>
                    <option value="COACH">Coach</option>
                    <option value="SUPER_ADMIN">Super Admin</option>
                  </>
                )}
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role}</p>
              )}
              {userRole === 'COACH' && (
                <p className="mt-1 text-xs text-gray-500">
                  As a coach, you can only invite clients
                </p>
              )}
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  type="text"
                  name="first_name"
                  id="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="John"
                />
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  type="text"
                  name="last_name"
                  id="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Doe"
                />
              </div>
            </div>

            {/* Full Name Field (read-only, auto-generated) */}
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                Full Name (Auto-generated)
              </label>
              <input
                type="text"
                name="full_name"
                id="full_name"
                value={formData.full_name}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 sm:text-sm"
                placeholder="Auto-generated from first and last name"
              />
            </div>

            {/* Coach Assignment Field */}
            <div>
              <label htmlFor="coach_id" className="block text-sm font-medium text-gray-700">
                Assign Coach
              </label>
              <select
                id="coach_id"
                name="coach_id"
                value={formData.coach_id}
                onChange={handleChange}
                disabled={userRole === 'COACH' && coaches.length === 1}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                  userRole === 'COACH' && coaches.length === 1 ? 'bg-gray-100' : ''
                }`}
              >
                {userRole !== 'COACH' && <option value="">No coach assigned</option>}
                {coaches.map((coach) => (
                  <option key={coach.id} value={coach.id}>
                    {coach.first_name && coach.last_name
                      ? `${coach.first_name} ${coach.last_name}`
                      : coach.full_name || coach.email}
                    {userRole === 'COACH' && coach.id === user?.id ? ' (You)' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {userRole === 'COACH'
                  ? 'As a coach, you are automatically assigned to this invite'
                  : 'Select a coach to assign this user to (optional)'
                }
              </p>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-300 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-400 disabled:opacity-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center transition-colors"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              'Send Invite'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InviteForm;